import { initialPlanningState } from '../types/planning';
import { flushAllDebounced } from '../utils/debounceRegistry';

import { useAppStore } from './appStore';

// Re-export the store hook for external use
export { useAppStore };

// Planning state hooks with defensive checks
export const usePlanningState = () => {
  const planningState = useAppStore((state) => state?.planningState);
  // Don't log on every render, only return initial state if truly undefined
  return planningState || initialPlanningState;
};

export const useUpdatePlanningField = () => {
  const updatePlanningField = useAppStore((state) => state?.updatePlanningField);
  // Return the function or a no-op, but don't log on every render
  return (
    updatePlanningField ||
    (() => {
      console.warn('Attempted to update planning field but store is not ready');
    })
  );
};
export const useUpdateMultiplePlanningFields = () => {
  const fn = useAppStore((state) => state?.updateMultiplePlanningFields);
  return fn || (() => console.warn('updateMultiplePlanningFields not ready'));
};
export const useClearPlanningState = () => {
  const fn = useAppStore((state) => state?.clearPlanningState);
  return fn || (() => console.warn('clearPlanningState not ready'));
};
export const useClearSceneDynamicFields = () => {
  const fn = useAppStore((state) => state?.clearSceneDynamicFields);
  return fn || (() => console.warn('clearSceneDynamicFields not ready'));
};
export const useClearScenePlanningFields = () => {
  const fn = useAppStore((state) => state?.clearScenePlanningFields);
  return fn || (() => console.warn('clearScenePlanningFields not ready'));
};
export const useClearBPlotFields = () => {
  const fn = useAppStore((state) => state?.clearBPlotFields);
  return fn || (() => console.warn('clearBPlotFields not ready'));
};

// Narrative state hooks
export const useNarrativeState = () => {
  const narrativeState = useAppStore((state) => state?.narrativeState);
  if (!narrativeState) {
    console.error('Narrative state is undefined');
    // This should not happen, but return a minimal valid state
    return {
      variables: {
        player_node2_statement_for_npc_memory: '',
      },
      nodes: {},
      currentSegmentId: 1 as const,
      currentNodeId: 'S1_Opening_Hook',
      segmentCompletion: {
        1: 'empty' as const,
        2: 'empty' as const,
        '3A': 'empty' as const,
        '3B': 'empty' as const,
      },
    };
  }
  return narrativeState;
};
export const useCurrentNode = () => {
  const currentNodeId = useAppStore((state) => state?.narrativeState?.currentNodeId);
  const nodes = useAppStore((state) => state?.narrativeState?.nodes);
  if (!currentNodeId || !nodes) {
    return undefined;
  }
  return nodes[currentNodeId];
};
export const useUpdateNode = () => {
  const fn = useAppStore((state) => state?.updateNode);
  return fn || (() => console.warn('updateNode not ready'));
};
export const useSetCurrentNode = () => {
  const fn = useAppStore((state) => state?.setCurrentNode);
  return fn || (() => console.warn('setCurrentNode not ready'));
};
export const useSetCurrentSegment = () => {
  const fn = useAppStore((state) => state?.setCurrentSegment);
  return fn || (() => console.warn('setCurrentSegment not ready'));
};
export const useUpdateNodeText = () => {
  const fn = useAppStore((state) => state?.updateNodeText);
  return fn || (() => console.warn('updateNodeText not ready'));
};
export const useUpdateNodeChoice = () => {
  const fn = useAppStore((state) => state?.updateNodeChoice);
  return fn || (() => console.warn('updateNodeChoice not ready'));
};
export const useSetVariable = () => {
  const fn = useAppStore((state) => state?.setVariable);
  return fn || (() => console.warn('setVariable not ready'));
};
export const useInitializeNarrative = () => {
  const fn = useAppStore((state) => state?.initializeNarrative);
  return fn || (() => console.warn('initializeNarrative not ready'));
};
export const useResetNarrativeState = () => {
  const fn = useAppStore((state) => state?.resetNarrativeState);
  return fn || (() => console.warn('resetNarrativeState not ready'));
};

// UI state hooks
export const useSelectedTab = () => useAppStore((state) => state?.selectedTab || 'tree');
export const useSetSelectedTab = () => {
  const fn = useAppStore((state) => state?.setSelectedTab);
  return fn || (() => console.warn('setSelectedTab not ready'));
};
export const useShowSettingsPopup = () => useAppStore((state) => state?.showSettingsPopup || false);
export const useSetShowSettingsPopup = () => {
  const fn = useAppStore((state) => state?.setShowSettingsPopup);
  return fn || (() => console.warn('setShowSettingsPopup not ready'));
};
export const useShowBPlotModal = () => useAppStore((state) => state?.showBPlotModal || false);
export const useSetShowBPlotModal = () => {
  const fn = useAppStore((state) => state?.setShowBPlotModal);
  return fn || (() => console.warn('setShowBPlotModal not ready'));
};
export const useHasBPlotBeenCompleted = () =>
  useAppStore((state) => state?.hasBPlotBeenCompleted || false);
export const useSetHasBPlotBeenCompleted = () => {
  const fn = useAppStore((state) => state?.setHasBPlotBeenCompleted);
  return fn || (() => console.warn('setHasBPlotBeenCompleted not ready'));
};
export const useHasSeenBPlotIntro = () => useAppStore((state) => state?.hasSeenBPlotIntro || false);
export const useSetHasSeenBPlotIntro = () => {
  const fn = useAppStore((state) => state?.setHasSeenBPlotIntro);
  return fn || (() => console.warn('setHasSeenBPlotIntro not ready'));
};
export const useShowWordLimitWarning = () =>
  useAppStore((state) => state?.showWordLimitWarning || false);
export const useSetShowWordLimitWarning = () => {
  const fn = useAppStore((state) => state?.setShowWordLimitWarning);
  return fn || (() => console.warn('setShowWordLimitWarning not ready'));
};
export const useShowWorldStateModal = () =>
  useAppStore((state) => state?.showWorldStateModal || false);
export const useSetShowWorldStateModal = () => {
  const fn = useAppStore((state) => state?.setShowWorldStateModal);
  return fn || (() => console.warn('setShowWorldStateModal not ready'));
};
export const usePendingWorldChoice = () =>
  useAppStore((state) => state?.pendingWorldChoice || null);
export const useSetPendingWorldChoice = () => {
  const fn = useAppStore((state) => state?.setPendingWorldChoice);
  return fn || (() => console.warn('setPendingWorldChoice not ready'));
};
export const useClickedChoiceIndex = () =>
  useAppStore((state) => state?.clickedChoiceIndex || null);
export const useSetClickedChoiceIndex = () => {
  const fn = useAppStore((state) => state?.setClickedChoiceIndex);
  return fn || (() => console.warn('setClickedChoiceIndex not ready'));
};

// Computed values
export const useIsSegmentUnlocked = () => {
  const fn = useAppStore((state) => state?.isSegmentUnlocked);
  return fn || (() => true); // Default to unlocked
};

// Flow actions
export const useFlowActions = () => {
  const flowActions = useAppStore((state) => state?.flowActions);
  return (
    flowActions || {
      goToStep: () => console.warn('flowActions not ready'),
      markStepCompleted: () => console.warn('flowActions not ready'),
      canNavigateToStep: () => false,
      validateAndRestoreFlow: () => console.warn('flowActions not ready'),
    }
  );
};

// Global actions
export const useClearAllState = () => {
  const fn = useAppStore((state) => state?.clearAllState);
  return fn || (() => console.warn('clearAllState not ready'));
};

// Undo/redo hooks
export const useUndoRedo = () => {
  try {
    const temporalState = useAppStore?.temporal?.getState?.();

    if (!temporalState) {
      return {
        undo: () => console.warn('Undo not available - temporal state not ready'),
        redo: () => console.warn('Redo not available - temporal state not ready'),
        canUndo: false,
        canRedo: false,
      };
    }

    // Wrap undo/redo to handle edge cases
    const safeUndo = () => {
      try {
        flushAllDebounced();
        if (temporalState.pastStates?.length > 0) {
          temporalState.undo();
        } else {
          console.warn('Cannot undo - no past states available');
        }
      } catch (error) {
        console.error('Error during undo:', error);
      }
    };

    const safeRedo = () => {
      try {
        flushAllDebounced();
        if (temporalState.futureStates?.length > 0) {
          temporalState.redo();
        } else {
          console.warn('Cannot redo - no future states available');
        }
      } catch (error) {
        console.error('Error during redo:', error);
      }
    };

    return {
      undo: safeUndo,
      redo: safeRedo,
      canUndo: (temporalState.pastStates?.length || 0) > 0,
      canRedo: (temporalState.futureStates?.length || 0) > 0,
    };
  } catch (error) {
    console.error('Error accessing temporal state:', error);
    return {
      undo: () => console.warn('Undo not available'),
      redo: () => console.warn('Redo not available'),
      canUndo: false,
      canRedo: false,
    };
  }
};

// Specific field hooks for planning state
export const useDynamicType = () => useAppStore((state) => state?.planningState?.dynamicType || '');
export const useRealConversation = () =>
  useAppStore((state) => state?.planningState?.realConversation || '');
export const useFictionalAnalysis = () =>
  useAppStore((state) => state?.planningState?.fictionalAnalysis || '');
export const usePlayerRole = () => useAppStore((state) => state?.planningState?.playerRole || '');
export const useNpcRole = () => useAppStore((state) => state?.planningState?.npcRole || '');
export const usePlayerName = () => useAppStore((state) => state?.planningState?.playerName || '');
export const useNpcName = () => useAppStore((state) => state?.planningState?.npcName || '');
export const usePlayerMotivation = () =>
  useAppStore((state) => state?.planningState?.playerMotivation || '');
export const useNpcMotivation = () =>
  useAppStore((state) => state?.planningState?.npcMotivation || '');
export const useAPlot = () => useAppStore((state) => state?.planningState?.aPlot || '');
export const useStakes = () => useAppStore((state) => state?.planningState?.stakes || '');
export const useLocationAtmosphere = () =>
  useAppStore((state) => state?.planningState?.locationAtmosphere || '');
export const useBPlotSeed = () => useAppStore((state) => state?.planningState?.bPlotSeed || '');
export const useCharacterNuanceNotes = () =>
  useAppStore((state) => state?.planningState?.characterNuanceNotes || '');

// Project management hooks
export const useProjects = () => useAppStore((s) => s?.projects || {});
export const useCurrentProjectId = () => useAppStore((s) => s?.currentProjectId || null);
export const useCreateProject = () => {
  const fn = useAppStore((s) => s?.createProject);
  return (
    fn ||
    (() => {
      console.warn('createProject not ready');
      return '';
    })
  );
};
export const useLoadProject = () => {
  const fn = useAppStore((s) => s?.loadProject);
  return fn || (() => console.warn('loadProject not ready'));
};
export const useRenameProject = () => {
  const fn = useAppStore((s) => s?.renameProject);
  return fn || (() => console.warn('renameProject not ready'));
};
export const useDuplicateProject = () => {
  const fn = useAppStore((s) => s?.duplicateProject);
  return (
    fn ||
    (() => {
      console.warn('duplicateProject not ready');
      return '';
    })
  );
};
export const useDeleteProject = () => {
  const fn = useAppStore((s) => s?.deleteProject);
  return fn || (() => console.warn('deleteProject not ready'));
};
export const useSaveCurrentProject = () => {
  const fn = useAppStore((s) => s?.saveCurrentProject);
  return fn || (() => console.warn('saveCurrentProject not ready'));
};
export const useSetProjectSyncFailed = () => {
  const fn = useAppStore((s) => s?.setProjectSyncFailed);
  return fn || (() => console.warn('setProjectSyncFailed not ready'));
};
export const useUpdateProjectSyncStatus = () => {
  const fn = useAppStore((s) => s?.updateProjectSyncStatus);
  return (
    fn ||
    ((projectId: string, status: 'synced' | 'pending' | 'conflict' | 'error') => {
      console.warn('updateProjectSyncStatus not ready', projectId, status);
    })
  );
};
export const useMergeProjects = () => {
  const fn = useAppStore((s) => s?.mergeProjects);
  return (
    fn ||
    ((incoming: Record<string, any>) => {
      console.warn('mergeProjects not ready', incoming);
    })
  );
};
export const useCalculateCompletionPercentage = () => {
  const fn = useAppStore((s) => s?.calculateCompletionPercentage);
  return (
    fn ||
    (() => {
      console.warn('calculateCompletionPercentage not ready');
      return 0;
    })
  );
};

// State validation hooks
export const useValidateState = () => {
  const fn = useAppStore((s) => s?.validateState);
  return (
    fn ||
    (() => {
      console.warn('validateState not ready');
      return { isValid: false, results: {} };
    })
  );
};

export const useRepairState = () => {
  const fn = useAppStore((s) => s?.repairState);
  return (
    fn ||
    (() => {
      console.warn('repairState not ready');
      return { planningRepaired: false, narrativeRepaired: false };
    })
  );
};
